import { valibotResolver } from "@hookform/resolvers/valibot";
import { Trash } from "@phosphor-icons/react";
import { type JSX, useEffect, useMemo, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { omit } from "valibot";

import {
  TemplateEditor,
  TemplatePreview,
  VoiceTemplate,
} from "features/templates/components";
import { TEMPLATE_TYPES } from "features/templates/constants";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";
import type { CreatedTemplate } from "features/templates/types";
import { getTemplateFromCreatedTemplate } from "features/templates/utils";

import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useCampaignContext } from "../../../../context/CampaignContext";
import { useCampaignFlowContext } from "../../../../context/CampaignFlowContext";
import useAudience from "../../../../datahooks/useAudience";
import useDetectLanguage from "../../../../datahooks/useDetectLanguage";
import DesktopLayout from "./DesktopLayout";
import { FlowTemplateProvider } from "./FlowTemplateContext";
import LanguageDropdown from "./LanguageDropdown";
import MobileLayout from "./MobileLayout";
import TemplateSelect from "./TemplateSelect";

import type { FlowTemplate as FlowTemplateType } from "../../../../types";

export default function FlowTemplate<
  FlowTemplateTypeGeneric extends FlowTemplateType,
>({
  flowTemplate,
  setFlowTemplate,
  handleRemove,
  isRemoveButtonHidden = false,
  actionDelayComponent = null,
  isMessageFlow = false,
}: {
  flowTemplate: FlowTemplateTypeGeneric;
  setFlowTemplate: (flowTemplate: FlowTemplateTypeGeneric) => void;
  handleRemove: () => void;
  isRemoveButtonHidden?: boolean;
  actionDelayComponent?: JSX.Element;
  isMessageFlow?: boolean;
}) {
  const { canEditFlow } = useCampaignFlowContext();
  const isDesktop = useTwBreakpoint("lg");
  const [isEditing, setIsEditing] = useState(false);

  const [previewTemplate, setPreviewTemplate] = useState<TemplateSchemaType>(
    flowTemplate.original_id
      ? getTemplateFromCreatedTemplate({
          ...flowTemplate,
          name: "",
        })
      : undefined,
  );

  const { detectLanguage } = useDetectLanguage();
  const { audience } = useAudience();
  const {
    id,
    owners,
    custom_variable_keys: customVariables,
  } = useCampaignContext();
  const { resetFlowError, flow } = useCampaignFlowContext();
  const { accounts } = useSelectedWorkspaceContext();

  const { validationSchema } = TEMPLATE_TYPES[flowTemplate.type];

  const form = useForm<TemplateSchemaType>({
    defaultValues: getTemplateFromCreatedTemplate({
      ...flowTemplate,
      name: "",
    }),
    resolver: valibotResolver(omit(validationSchema, ["name"])),
  });

  useEffect(() => {
    if (flowTemplate) {
      const template = getTemplateFromCreatedTemplate({
        ...flowTemplate,
        name: "",
      });
      form.reset(template);
      if (flowTemplate.original_id) {
        setPreviewTemplate(template);
      }
    }
  }, [flowTemplate]);

  function updateFlowTemplate(
    template: TemplateSchemaType,
    flowTemplateData: { original_id: string; edited: boolean },
  ) {
    const { name: _name, ...templateRest } = template;

    setFlowTemplate(
      (flowTemplate.type === "MESSAGE_TEMPLATE" ||
      flowTemplate.type === "VOICE_TEMPLATE"
        ? {
            ...templateRest,
            ...flowTemplateData,
            delay: flowTemplate.delay,
          }
        : { ...templateRest, ...flowTemplateData }) as FlowTemplateTypeGeneric,
    );
  }
  function handleSelectTemplate(selectedTemplate: CreatedTemplate) {
    if (selectedTemplate.type !== "VOICE_TEMPLATE") {
      setPreviewTemplate(getTemplateFromCreatedTemplate(selectedTemplate));

      if (!flow.is_manual_language) {
        detectLanguage({ text: selectedTemplate.message });
      }
    }
    const template = getTemplateFromCreatedTemplate(selectedTemplate);
    form.reset(template);

    const { name: _name, ...templateRest } = template;
    setFlowTemplate(
      (isMessageFlow
        ? {
            ...templateRest,
            original_id: selectedTemplate.id,
            edited: false,
            delay:
              flowTemplate && "delay" in flowTemplate ? flowTemplate.delay : 0,
          }
        : {
            ...templateRest,
            original_id: selectedTemplate.id,
            edited: false,
          }) as FlowTemplateTypeGeneric,
    );

    // Remove from invalidFlows
    if (resetFlowError) {
      resetFlowError();
    }
  }

  function onSubmit(data: TemplateSchemaType) {
    if (data.type !== "VOICE_TEMPLATE") {
      setPreviewTemplate(data);
      detectLanguage({ text: data.message });
    }

    updateFlowTemplate(data, {
      original_id: flowTemplate.original_id,
      edited: true,
    });

    setIsEditing(false);
  }

  function handleDiscard() {
    form.reset(
      getTemplateFromCreatedTemplate({
        ...flowTemplate,
        name: "",
      }),
    );
    setIsEditing(false);
  }

  function handleSave() {
    form.handleSubmit(onSubmit)();
  }

  function handleStartEdit() {
    setIsEditing(true);
  }

  const contextValue = useMemo(
    () => ({
      flowTemplate,
      isEditing,
      handleDiscard,
      handleSelectTemplate,
      handleSave,
      handleStartEdit,
    }),
    [
      flowTemplate,
      isEditing,
      handleDiscard,
      handleSelectTemplate,
      handleSave,
      handleStartEdit,
    ],
  );

  const targetProfiles = audience?.slice(0, 3);
  const updatedTemplate = form.watch();

  const templatePreviewComponent = (
    <TemplatePreview
      previewTemplate={previewTemplate}
      updatedTemplate={updatedTemplate}
      onUpdatePreview={() => setPreviewTemplate(updatedTemplate)}
      targetProfiles={targetProfiles}
      senderProfiles={accounts.filter(({ id }) => owners.includes(id))}
      campaignData={{
        campaignId: id,
        sourceLanguageCode: flow.source_language,
        isTranslateEnabled: flow.translate,
      }}
      hasError={
        "message" in form.formState.errors ||
        (updatedTemplate.type === "VOICE_TEMPLATE" &&
          "attachments" in form.formState.errors)
      }
    />
  );

  const templateEditorComponent =
    updatedTemplate.type === "VOICE_TEMPLATE" ? (
      <VoiceTemplate
        form={form as UseFormReturn<VoiceTemplateSchemaType>}
        isEditable={isEditing}
        className="grow"
      />
    ) : (
      <TemplateEditor
        form={
          form as UseFormReturn<
            Exclude<TemplateSchemaType, VoiceTemplateSchemaType>
          >
        }
        isEditable={isEditing}
        customVariables={customVariables}
        autoFocus
        placeholderText={
          !flowTemplate &&
          "Select a template first. The text will show a preview here when selected"
        }
        languageDropdownComponent={
          <LanguageDropdown flowTemplate={flowTemplate} />
        }
      />
    );

  const canShowRemoveButton = !(
    isRemoveButtonHidden || flowTemplate.type === "INMAIL_TEMPLATE"
  );
  const removeButtonComponent = canShowRemoveButton && (
    <Button
      variant="outline-danger"
      className="ml-auto"
      leftIcon={<Trash />}
      onClick={handleRemove}
      disabled={!canEditFlow}
    >
      Remove
    </Button>
  );
  const templateSelectComponent = (
    <TemplateSelect
      selectTemplate={handleSelectTemplate}
      isEditing={isEditing}
      flowTemplate={flowTemplate}
      isMessageFlow={isMessageFlow}
    />
  );

  const Component = isDesktop ? DesktopLayout : MobileLayout;

  return (
    <FlowTemplateProvider value={contextValue}>
      <Component
        actionDelayComponent={actionDelayComponent}
        templatePreviewComponent={templatePreviewComponent}
        templateEditorComponent={templateEditorComponent}
        removeButtonComponent={removeButtonComponent}
        templateSelectComponent={templateSelectComponent}
      />
    </FlowTemplateProvider>
  );
}
